//CheckoutSuccess.tsx
import React, { useEffect, useState } from 'react';
import { resolveFrontendApiBase } from './utils/apiBase';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as fbPixel from './utils/fbPixel';
import {
  Button,
  Alert,
} from 'antd';
import {
  CheckCircleOutlined,
  PlayCircleOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  GiftOutlined,
  BookOutlined,
  FacebookOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { getFacebookCookies } from './utils/trackingCookies';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseAuth';
import { getAuthHeaders } from './utils/authHeaders';
import AppPageHeader from './components/AppPageHeader';
import './CheckoutSuccess.css';

const API_BASE = resolveFrontendApiBase();

declare global {
  interface Window {
    __SCRIBE_CHECKOUT_SESSION__?: string;
  }
}

const readRecoveryStorage = (key: string): string | null => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};

const boundedSessionId = (value: string | null | undefined): string | null =>
  value && /^[A-Za-z0-9_-]{1,256}$/.test(value) ? value : null;

const CheckoutSuccess: React.FC<{ onSignIn?: () => void }> = ({ onSignIn }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [purchaseValue, setPurchaseValue] = useState<number | null>(null);
  const [ensureLoading, setEnsureLoading] = useState(false);
  const [ensureError, setEnsureError] = useState<string | null>(null);
  const [ensureRetry, setEnsureRetry] = useState(0);
  const [signedInUser, setSignedInUser] = useState(auth.currentUser);
  const [sessionId] = useState(() => {
    const id = boundedSessionId(searchParams.get('session_id'))
      || boundedSessionId(window.__SCRIBE_CHECKOUT_SESSION__)
      || boundedSessionId(readRecoveryStorage('checkout-recovery-session'));
    if (id) {
      window.__SCRIBE_CHECKOUT_SESSION__ = id;
      try { sessionStorage.setItem('checkout-recovery-session', id); } catch {}
    }
    return id;
  });
  useEffect(() => onAuthStateChanged(auth, setSignedInUser), []);
  useEffect(() => {
    const clean = new URL(window.location.href);
    ['session_id', 'claimToken', 'registrationToken', 'customToken'].forEach(key => clean.searchParams.delete(key));
    window.history.replaceState(window.history.state, '', `${clean.pathname}${clean.search}${clean.hash}`);
  }, []);
  const plan = searchParams.get('plan') || 'starter';

  useEffect(() => {
    if (!sessionId || !signedInUser) return;
    let active = true;
    void (async () => {
      const response = await fetch(`${API_BASE}/get-checkout-session-details?session_id=${encodeURIComponent(sessionId)}`, {
        headers: await getAuthHeaders(signedInUser),
      });
      if (response.ok) {
        const data = await response.json();
        if (active && typeof data.amount_total === 'number') setPurchaseValue(data.amount_total / 100);
      }
    })().catch(() => {});
    return () => { active = false; };
  }, [sessionId, signedInUser, ensureRetry]);

  useEffect(() => {
    const track = async () => {
      if (!sessionId || purchaseValue === null) return;
      // Ensure the pixel script is loaded and initialized
      await fbPixel.ensureInit();
      // Fire PageView so Pixel Helper recognizes pixel on this page too
      fbPixel.pageview();

      const { fbp, fbc } = getFacebookCookies();

      // For subscription checkout from landing page we send Subscribe.
      // Use the same event_id format as the server for dedup.
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(sessionId));
      const subscribeEventId = `subscribe_${Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')}`;

      fbPixel.event(
        'Subscribe',
        {
          value: purchaseValue,
          currency: 'USD',
          content_ids: [plan],
          content_type: 'subscription',
          fbp,
          fbc,
        },
        subscribeEventId
      );
    };
    track();
  }, [sessionId, plan, purchaseValue]);

  // Payment recovery follows normal sign-in; checkout never supplies login credentials.
  useEffect(() => {
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const ensure = async (attempt = 0) => {
      if (!sessionId || !signedInUser) return;
      setEnsureLoading(true);
      setEnsureError(null);
      try {
        const resp = await fetch(`${API_BASE}/lazy/ensure-account-for-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...await getAuthHeaders(signedInUser) },
          body: JSON.stringify({ session_id: sessionId, claimToken: readRecoveryStorage(`checkout-claim:${sessionId}`) }),
        });
        if (cancelled) return;
        if (!resp.ok) {
          setEnsureError(resp.status === 403
            ? 'Sign in with the verified email used for this purchase, in the browser that started checkout. Contact support if you need help recovering it.'
            : 'Your payment is saved. Account or credit recovery could not finish yet. Retry below or contact support.');
          return;
        }
        const data = await resp.json();
        if (cancelled) return;
        if (data.creditReconciliationPending) {
          setEnsureError(attempt < 3
            ? 'Your purchased credits are still being added. Retrying automatically…'
            : 'Your purchased credits could not be added yet. Retry below, or contact support if this continues.');
          if (attempt < 3) retryTimer = setTimeout(() => void ensure(attempt + 1), 2000 * (attempt + 1));
        } else {
          try { sessionStorage.removeItem('checkout-recovery-session'); } catch {}
          if (window.__SCRIBE_CHECKOUT_SESSION__ === sessionId) delete window.__SCRIBE_CHECKOUT_SESSION__;
          // Keep the bounded purchase claim for explicit receipt retries until expiry.
        }
      } catch {
        if (!cancelled) setEnsureError('Your payment is saved. Retry account and credit recovery below.');
      } finally {
        if (!cancelled) setEnsureLoading(false);
      }
    };
    void ensure();
    return () => { cancelled = true; if (retryTimer) clearTimeout(retryTimer); };
  }, [sessionId, signedInUser, ensureRetry]);

  const ensureAlert = !signedInUser ? (
    <Alert type="info" message="Sign in or create an account with the email used at checkout, then verify that email to claim your purchase." showIcon
      action={<Button onClick={onSignIn}>Sign in or create account</Button>} />
  ) : ensureError ? (
    <Alert type="warning" message={ensureError} showIcon className="checkout-success-alert"
      action={<Button disabled={ensureLoading} onClick={() => setEnsureRetry(value => value + 1)}>Retry account and credits</Button>} />
  ) : null;

  const quickActions = [
    {
      icon: <BookOutlined />,
      title: 'Translate First Book',
      description: 'Upload and translate your first book',
      onClick: () => navigate('/manage-books'),
      primary: true,
    },
    {
      icon: <PlayCircleOutlined />,
      title: 'Watch Tutorials',
      description: 'Learn how to use ScribeShadow effectively',
      onClick: () => navigate('/support#videoTutorial'),
    },
    {
      icon: <QuestionCircleOutlined />,
      title: 'Get Support',
      description: 'Browse FAQs and help resources',
      onClick: () => navigate('/support'),
    },
  ];

  const communityLinks = [
    {
      icon: <MessageOutlined />,
      title: 'Discord Community',
      description: 'Join our active Discord server for real-time discussions',
      onClick: () => window.open('https://discord.com/invite/k7vnT38vPq', '_blank'),
      tone: 'discord',
    },
    {
      icon: <FacebookOutlined />,
      title: 'Facebook Group',
      description: 'Connect with authors in our Facebook community',
      onClick: () => window.open('https://www.facebook.com/groups/scribeshadow', '_blank'),
      tone: 'facebook',
    },
  ];

  return (
    <main className="checkout-success-page">
      <div className="checkout-success-shell">
        <AppPageHeader
          kicker="Checkout complete"
          title="Welcome to ScribeShadow!"
          description="Sign in to finish account and payment recovery."
          metrics={
            <div className="checkout-success-status" aria-live="polite">
              <span className="checkout-success-status__icon">
                <CheckCircleOutlined />
              </span>
              <span>
                <strong>{plan}</strong>
                {purchaseValue !== null && <small>${purchaseValue.toFixed(2)} confirmed</small>}
              </span>
            </div>
          }
          actions={
            <>
              <Button type="primary" icon={<BookOutlined />} onClick={() => navigate('/manage-books')}>
                Start Translating
              </Button>
              <Button onClick={() => navigate('/account')}>
                View Account
              </Button>
            </>
          }
        />

        {ensureAlert}

        {/* Quick Actions Section */}
        <section className="checkout-success-section" aria-labelledby="checkout-success-next">
          <div className="checkout-success-section__header">
            <h2 id="checkout-success-next">Next steps</h2>
            <p>Use the shortcuts below to get to the most common post-checkout tasks.</p>
          </div>
          <div className="checkout-success-task-list">
            {quickActions.map((action) => (
              <button
                type="button"
                className={`checkout-success-task ${action.primary ? 'checkout-success-task--primary' : ''}`}
                key={action.title}
                onClick={action.onClick}
              >
                <span className="checkout-success-task__icon">{action.icon}</span>
                <span className="checkout-success-task__copy">
                  <strong>{action.title}</strong>
                  <small>{action.description}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Community Section */}
        <section className="checkout-success-section" aria-labelledby="checkout-success-community">
          <div className="checkout-success-section__header checkout-success-section__header--inline">
            <span className="checkout-success-section__icon">
              <TeamOutlined />
            </span>
            <div>
              <h2 id="checkout-success-community">Join Our Community</h2>
              <p>Connect with authors, share translation workflows, and get help from other customers.</p>
            </div>
          </div>
          <div className="checkout-success-community-list">
            {communityLinks.map((link) => (
              <button
                type="button"
                className={`checkout-success-community checkout-success-community--${link.tone}`}
                key={link.title}
                onClick={link.onClick}
              >
                <span className="checkout-success-community__icon">{link.icon}</span>
                <span>
                  <strong>{link.title}</strong>
                  <small>{link.description}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        {/* Referral Alert */}
        <Alert
          className="checkout-success-referral"
          message={<strong>Earn Forever Credits</strong>}
          description={
            <div className="checkout-success-referral__content">
              <p>Share ScribeShadow with fellow authors and earn rewards:</p>
              <ul>
                <li>They receive bonus forever credits after their first subscription payment.</li>
                <li>You earn 25% of their plan credits, with annual plans doubling your payout.</li>
                <li>Forever Credits never expire.</li>
              </ul>
            </div>
          }
          type="success"
          showIcon={false}
          action={
            <Button
              type="primary"
              icon={<GiftOutlined />}
              onClick={() => navigate('/account')}
            >
              Get Your Referral Code
            </Button>
          }
        />

        {/* Footer Info */}
        <footer className="checkout-success-footer">
          <span>Need help?</span>
          <Button type="link" onClick={() => navigate('/support')}>
            Visit Support Center
          </Button>
          <span>or email support@scribeshadow.com</span>
        </footer>
      </div>
    </main>
  );
};

export default CheckoutSuccess;
